/**
 * creditsContext.js — RC 1.38.B.1 Commercial Transparency (corrected).
 *
 * Single source of truth for the candidate commercial state.
 *
 *   Real source:   GET /api/account/plan          (RC 1.33.B+)
 *                  GET /api/jobs/analyze-fit/usage (RC 1.31 free-fit)
 *
 *   Legacy (DO NOT USE):
 *     /api/credits/me — returns the quarantined `credit_action_prices`
 *     ledger and a wallet balance that bears no relationship to the
 *     user's actual entitlement. See `core/usage.py:53` — *legacy
 *     credit_action_prices … MUST NOT be relied on*.
 *
 * The provider exposes a single, plan-aware ledger:
 *
 *   {
 *     planCode:               'base' | 'focus' | 'flow' | null,
 *     status:                 'active' | 'pending' | 'expired' | null,
 *     hasEntitlement:         boolean,
 *     includedUsageRemaining: number | null,
 *     extraUsageRemaining:    number | null,
 *     usageCosts:             {analyze_fit, cv_improve, cover_letter,
 *                              application_pack},
 *     freeFit:                {limit, used, remaining, ...} | null,
 *     loading:                boolean,
 *     refresh:                () => Promise<void>,
 *   }
 *
 * Hard rails: read-only; zero pricing-value change; zero Stripe call;
 * zero schema migration.
 *
 * The legacy filename is preserved so existing import paths still
 * resolve — but the implementation is fully rewritten and no longer
 * touches `/api/credits/me`.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from './api';

const DEFAULT_VALUE = {
  planCode:               null,
  status:                 null,
  hasEntitlement:         false,
  includedUsageRemaining: null,
  extraUsageRemaining:    null,
  usageCosts:             null,
  freeFit:                null,
  loading:                false,
  refresh:                () => {},
};

const CreditsContext = createContext(DEFAULT_VALUE);

export function CreditsProvider({ children }) {
  const [planCode, setPlanCode]                 = useState(null);
  const [status, setStatus]                     = useState(null);
  const [hasEntitlement, setHasEntitlement]     = useState(false);
  const [includedRemaining, setIncludedRemaining] = useState(null);
  const [extraRemaining, setExtraRemaining]     = useState(null);
  const [usageCosts, setUsageCosts]             = useState(null);
  const [freeFit, setFreeFit]                   = useState(null);
  const [loading, setLoading]                   = useState(false);
  const location = useLocation();

  // RC 1.49.4 Tier 1 — refresh strategy:
  //   • Initial mount: always fetch (we have no cached state yet).
  //   • Subsequent navigations: fetch only if the last successful
  //     fetch is older than `STALE_MS`, OR a mutation has flipped
  //     the dirty flag (callable via `refresh()`). This eliminates
  //     2 API calls per navigation when the user is just clicking
  //     between routes.
  //   • Explicit `refresh()` (called after a paid action) bypasses
  //     the staleness check by resetting `_lastFetchAt = 0`.
  // The dirty-flag is module-scoped so any code path can mark the
  // credits cache stale — e.g. after a successful generate, the
  // wizard calls `refresh()`.
  const _lastFetchAtRef = useRef(0);
  const STALE_MS = 60_000;

  const fetchNow = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, freeFitRes] = await Promise.allSettled([
        apiFetch('/api/account/plan'),
        apiFetch('/api/jobs/analyze-fit/usage'),
      ]);

      if (planRes.status === 'fulfilled') {
        const d = planRes.value || {};
        setPlanCode(d.plan_code || null);
        setStatus(d.status || null);
        setHasEntitlement(!!d.has_entitlement);
        setIncludedRemaining(
          typeof d.included_usage_remaining === 'number' ? d.included_usage_remaining : null,
        );
        setExtraRemaining(
          typeof d.extra_usage_remaining === 'number' ? d.extra_usage_remaining : null,
        );
        const costs = d.usage_costs;
        if (costs && typeof costs === 'object') {
          // Coerce defensively — backend returns ints but a shape
          // regression should not put strings into arithmetic paths.
          const sanitised = {};
          for (const [k, v] of Object.entries(costs)) {
            if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
              sanitised[k] = v;
            }
          }
          setUsageCosts(Object.keys(sanitised).length ? sanitised : null);
        }
      }

      if (freeFitRes.status === 'fulfilled') {
        const f = freeFitRes.value || {};
        const limit     = Number(f.limit);
        const used      = Number(f.used);
        const remaining = Number(f.remaining);
        if ([limit, used, remaining].every(Number.isFinite)) {
          setFreeFit({
            limit, used, remaining,
            reset_at_utc: f.reset_at_utc || null,
            plan: f.plan || null,
          });
        }
      }
      _lastFetchAtRef.current = Date.now();
    } catch {
      // Silent — unauthenticated or endpoint down. Consumers render
      // their fallback (no pill, no price label).
    } finally {
      setLoading(false);
    }
  }, []);

  // Public refresh — invoked by callers AFTER a charge / generate /
  // pack action. Bypasses the staleness check.
  const refresh = useCallback(async () => {
    _lastFetchAtRef.current = 0;
    await fetchNow();
  }, [fetchNow]);

  // Refresh on initial mount, then only on subsequent navigations
  // where we're past the staleness window. This stops the prior
  // behaviour of firing 2 API calls on every single route change.
  useEffect(() => {
    const stale = (Date.now() - _lastFetchAtRef.current) >= STALE_MS;
    if (stale) fetchNow();
  }, [fetchNow, location.pathname]);

  const value = useMemo(
    () => ({
      planCode,
      status,
      hasEntitlement,
      includedUsageRemaining: includedRemaining,
      extraUsageRemaining:    extraRemaining,
      usageCosts,
      freeFit,
      loading,
      refresh,
    }),
    [
      planCode, status, hasEntitlement,
      includedRemaining, extraRemaining,
      usageCosts, freeFit, loading, refresh,
    ],
  );

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  return useContext(CreditsContext);
}

/**
 * Pure helpers — split out into `creditsHelpers.js` so they're
 * trivial to unit-test in Jest without mounting React. Re-exported
 * here to keep the public import surface stable.
 */
export {
  getActionCost,
  isInsufficient,
  formatBalance,
  totalUsageRemaining,
  isPaidPlan,
} from './creditsHelpers';
